import { useEffect, useRef, useState } from "react";
import { useDebounce } from "use-debounce";
import OutsideClickHandler from "react-outside-click-handler/build/OutsideClickHandler";
import { FiChevronDown } from "react-icons/fi";

import TagService from "src/Services/API/tag.service";
import { handleError } from "src/Common/function";

// Two tags are "the same" when they share an id (persisted) or, for
// not-yet-created tags, a case-insensitive name. Mirrors the technician app's
// TagInput so both clients produce the same { id, name } shape the backend
// assigns directly onto wo_attachment.tags.
const isSameTag = (a, b) => {
  if (a?.id && b?.id) return a.id === b.id;
  return a?.name?.trim()?.toLowerCase() === b?.name?.trim()?.toLowerCase();
};

/**
 * Tag picker for the form-upload flows. Searches existing tags as you type and
 * lets you create a new one inline. `selectedTags` / `onChange` carry the full
 * tag entities ({ id, name }) the upload metadata expects.
 */
const TagInput = ({ selectedTags = [], onChange }) => {
  const [text, setText] = useState("");
  const [debouncedText] = useDebounce(text, 400);
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    TagService.searchTags(debouncedText)
      .then((res) => {
        if (res.status === 200) setOptions(res.data || []);
      })
      .catch((err) => handleError(err));
  }, [debouncedText]);

  const trimmed = text.trim();
  const hasExactMatch = options.some(
    (tag) => tag?.name?.trim()?.toLowerCase() === trimmed.toLowerCase(),
  );

  const addTag = (tag) => {
    if (selectedTags.some((selected) => isSameTag(selected, tag))) return;
    onChange?.([...selectedTags, tag]);
  };

  const removeTag = (tag) => {
    onChange?.(selectedTags.filter((selected) => !isSameTag(selected, tag)));
  };

  const handleCreateTag = async () => {
    if (!trimmed) return;
    try {
      const res = await TagService.createTag({ name: trimmed });
      if ((res.status === 200 || res.status === 201) && res.data?.name) {
        setOptions((prev) => [...prev, res.data]);
        addTag(res.data);
        setText("");
        setOpen(false);
      }
    } catch (err) {
      handleError(err);
    }
  };

  return (
    <OutsideClickHandler onOutsideClick={() => setOpen(false)}>
      <div className="relative w-full">
        <div
          onClick={() => {
            setOpen(true);
            inputRef.current?.focus();
          }}
          className="w-full min-h-[40px] flex flex-wrap items-center gap-2 px-3 py-2 border border-[#E5E8F3] rounded-[6px] cursor-text"
        >
          {selectedTags.map((tag) => (
            <span
              key={tag.id ?? tag.name}
              className="inline-flex items-center gap-1 h-[24px] px-2 rounded-[6px] bg-[#EAF3FF] font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] text-[#0058E5]"
            >
              {tag.name}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeTag(tag);
                }}
                className="text-[#0058E5]"
              >
                &#10005;
              </button>
            </span>
          ))}
          <input
            ref={inputRef}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder="Add a tag"
            autoComplete="off"
            className="flex-1 min-w-[80px] h-[24px] outline-none font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332] placeholder:text-[#868DA6]"
          />
          <FiChevronDown className="text-[#868DA6]" size={18} />
        </div>

        {open && (options.length > 0 || trimmed.length > 0) && (
          <ul className="absolute z-20 mt-1 w-full max-h-[200px] overflow-y-auto bg-white border border-[#E5E8F3] rounded-[8px] shadow-[0px_4px_12px_0px_rgba(10,12.67,18,0.10)]">
            {options.map((tag) => (
              <li
                key={tag.id ?? tag.name}
                onClick={() => {
                  addTag(tag);
                  setText("");
                  setOpen(false);
                }}
                className="px-3 py-2 cursor-pointer hover:bg-[#F8F9FE] font-OpenSansRegular text-[14px] text-[#1A2332]"
              >
                {tag.name}
              </li>
            ))}
            {trimmed.length > 0 && !hasExactMatch && (
              <li
                onClick={handleCreateTag}
                className="px-3 py-2 cursor-pointer hover:bg-[#F8F9FE] font-OpenSansSemiBold text-[14px] text-[#0065FD]"
              >
                + Add "{trimmed}"
              </li>
            )}
          </ul>
        )}
      </div>
    </OutsideClickHandler>
  );
};

export default TagInput;
